import type { RepeatingSchedule, ScheduleSummary, TriggerDetail } from '@/types/schedule';

import { enumerateCronExecutionsInUtcRange } from './recurringCronValidation';
import {
  computeDailyNextRunInstantFromTimeUtc,
  parseApiDateTimeInTimeZone,
  parseScheduleApiDateTime,
} from './scheduleDateTime';
import { wallClockInTimeZoneToUtc } from './timezoneData';

const MS_PER_DAY = 86_400_000;
const AWS_START_DATE_BUFFER_MS = 5 * 60 * 1000;
const MAX_UPCOMING_SCHEDULE_ROWS = 10_000;

/** Format a UTC instant as getTriggerDetails-style "MM-DD-YYYY h:mm A" in UTC wall clock. */
export function formatUtcApiDateTime(instant: Date): string {
  const month = String(instant.getUTCMonth() + 1).padStart(2, '0');
  const day = String(instant.getUTCDate()).padStart(2, '0');
  const year = instant.getUTCFullYear();
  let hours = instant.getUTCHours();
  const minutes = String(instant.getUTCMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${month}-${day}-${year} ${hours}:${minutes} ${ampm}`;
}

function calendarPartsInTimeZone(instant: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
  }).formatToParts(instant);

  const get = (type: Intl.DateTimeFormatPartTypes): number =>
    parseInt(parts.find((part) => part.type === type)?.value ?? '0', 10);

  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour') % 24,
    minute: get('minute'),
  };
}

/** Mirrors transformer_api/schedule/startdate.go GetStartDateForRepeatingScheduleAt. */
export function getStartDateForRepeatingScheduleAt(
  startingAt: string,
  timeZone: string | undefined,
  now: Date = new Date()
): Date | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(startingAt?.trim() ?? '');
  if (!match) {
    return null;
  }

  const hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  if (hour > 23 || minute > 59) {
    return null;
  }

  const tz = timeZone?.trim();
  if (tz) {
    // startingAt is UTC HH:MM (from buildRepeatingSchedulePayload) — mirror Go startdate.go:
    // parse UTC wall clock, derive author-local hour/minute via .In(location), anchor today in TZ.
    const nowInTz = calendarPartsInTimeZone(now, tz);
    const utcWall = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), hour, minute, 0, 0)
    );
    const localWall = calendarPartsInTimeZone(utcWall, tz);

    let start = wallClockInTimeZoneToUtc(
      nowInTz.year,
      nowInTz.month,
      nowInTz.day,
      localWall.hour,
      localWall.minute,
      tz
    );
    if (start.getTime() <= now.getTime() + AWS_START_DATE_BUFFER_MS) {
      start = new Date(start.getTime() + MS_PER_DAY);
    }
    return start;
  }

  let start = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), hour, minute, 0, 0)
  );
  if (start.getTime() <= now.getTime() + AWS_START_DATE_BUFFER_MS) {
    start = new Date(start.getTime() + MS_PER_DAY);
  }
  return start;
}

function repeatingIntervalMs(repeating: RepeatingSchedule): number {
  return repeating.frequencyUnit === 'hour'
    ? repeating.frequency * 3_600_000
    : repeating.frequency * 60_000;
}

function buildUpcomingScheduleDetail(schedule: ScheduleSummary, instant: Date): TriggerDetail {
  return {
    executionTime: formatUtcApiDateTime(instant),
    transformerName: schedule.transformerName,
    environmentName: schedule.environmentName,
    environmentId: schedule.environmentId,
    baseId: schedule.baseId,
    executionStatus: 'Scheduled',
    isSchedulesPaused: schedule.isSchedulesPaused,
    isUpcomingSchedule: true,
  };
}

function generateCronUpcomingRuns(
  schedule: ScheduleSummary,
  windowStart: Date,
  rangeEnd: Date
): TriggerDetail[] {
  const expression = schedule.cronExpression?.trim();
  if (!expression) {
    return [];
  }

  return enumerateCronExecutionsInUtcRange(expression, windowStart, rangeEnd)
    .slice(0, MAX_UPCOMING_SCHEDULE_ROWS)
    .map((instant) => buildUpcomingScheduleDetail(schedule, instant));
}

function generateDailyUpcomingRuns(
  schedule: ScheduleSummary,
  windowStart: Date,
  rangeEnd: Date
): TriggerDetail[] {
  const timeUTC = schedule.dailySchedule?.timeUTC?.trim();
  if (!timeUTC) {
    return [];
  }

  const details: TriggerDetail[] = [];
  let slot = computeDailyNextRunInstantFromTimeUtc(timeUTC, windowStart);
  while (
    slot &&
    slot.getTime() <= rangeEnd.getTime() &&
    details.length < MAX_UPCOMING_SCHEDULE_ROWS
  ) {
    if (slot.getTime() > windowStart.getTime()) {
      details.push(buildUpcomingScheduleDetail(schedule, slot));
    }
    slot = new Date(slot.getTime() + MS_PER_DAY);
  }

  return details;
}

function getRepeatingAnchorInstant(schedule: ScheduleSummary, now: Date): Date | null {
  if (schedule.nextRun?.trim()) {
    const nextRun = parseScheduleApiDateTime(schedule.nextRun, schedule, 'nextRun');
    if (!isNaN(nextRun.getTime())) {
      return nextRun;
    }
  }

  const repeating = schedule.repeatingSchedule;
  if (!repeating) {
    return null;
  }

  if (repeating.startingAt?.trim()) {
    return getStartDateForRepeatingScheduleAt(repeating.startingAt, repeating.timeZone, now);
  }

  return now;
}

function generateRepeatingUpcomingRuns(
  schedule: ScheduleSummary,
  windowStart: Date,
  rangeEnd: Date,
  now: Date
): TriggerDetail[] {
  const repeating = schedule.repeatingSchedule;
  if (!repeating) {
    return [];
  }

  const intervalMs = repeatingIntervalMs(repeating);
  if (intervalMs < 1) {
    return [];
  }

  let anchor = getRepeatingAnchorInstant(schedule, now);
  if (!anchor || isNaN(anchor.getTime())) {
    return [];
  }

  while (anchor.getTime() < windowStart.getTime()) {
    anchor = new Date(anchor.getTime() + intervalMs);
  }

  const details: TriggerDetail[] = [];
  let slot = anchor;
  while (slot.getTime() <= rangeEnd.getTime() && details.length < MAX_UPCOMING_SCHEDULE_ROWS) {
    if (slot.getTime() > windowStart.getTime()) {
      details.push(buildUpcomingScheduleDetail(schedule, slot));
    }
    slot = new Date(slot.getTime() + intervalMs);
  }

  return details;
}

/**
 * Generate upcoming schedule run rows for max(windowStart, now) → rangeEnd.
 * Uses schedule summary config; status is always Scheduled (not ES history).
 */
export function generateUpcomingScheduleRuns(
  schedules: ScheduleSummary[],
  rangeStart: Date,
  rangeEnd: Date,
  now: Date = new Date()
): TriggerDetail[] {
  const windowStart = new Date(Math.max(rangeStart.getTime(), now.getTime()));
  if (windowStart.getTime() > rangeEnd.getTime()) {
    return [];
  }

  const details: TriggerDetail[] = [];

  for (const schedule of schedules) {
    if (schedule.isSchedulesPaused) {
      continue;
    }

    if (schedule.cronExpression?.trim()) {
      details.push(...generateCronUpcomingRuns(schedule, windowStart, rangeEnd));
    } else if (schedule.dailySchedule?.timeUTC) {
      details.push(...generateDailyUpcomingRuns(schedule, windowStart, rangeEnd));
    } else if (schedule.repeatingSchedule) {
      details.push(...generateRepeatingUpcomingRuns(schedule, windowStart, rangeEnd, now));
    }
  }

  return details.sort((left, right) => {
    const leftMs = parseApiDateTimeInTimeZone(left.executionTime, 'UTC').getTime();
    const rightMs = parseApiDateTimeInTimeZone(right.executionTime, 'UTC').getTime();
    return leftMs - rightMs;
  });
}
